import { fileChunkSize } from 'constants/fileChunkSize';
import { useState } from 'react';
import { apiClient } from 'utils';
import { useResponseHandler } from 'utils/service/responseHandler';
import { v4 } from 'uuid';

export const useChunkUpload = (name: string) => {
  const { responseHandler } = useResponseHandler();

  const [progress, setProgress] = useState(0);

  const retryUpload = async (
    chunkNumber: number,
    chunk: Blob,
    ImportId: string,
    totalChunks: number,
    onProgress: ((pr: number) => void) | undefined
  ) => {
    let retries = 3;
    let lastError: Error | null = null;

    while (retries > 0) {
      try {
        const formData = new FormData();
        formData.append('ImportId', ImportId);
        formData.append('ChunkNumber', (chunkNumber + 1).toString());
        formData.append('Chunk', chunk);

        const apiResponse = await apiClient({
          method: 'POST',
          url: 'Chunk/Import',
          body: formData,
          withContentType: false,
        });

        const response = await responseHandler(apiResponse, {
          groupName: `${name} - usePOSTChunkedUpload`,
        });

        const overallProgress = ((chunkNumber + 1) / totalChunks) * 100;

        setProgress(Math.round(overallProgress));
        if (onProgress) {
          onProgress(Math.round(overallProgress));
        }
        return response;
      } catch (error: unknown) {
        lastError = error instanceof Error ? error : new Error('Unknown error');
        retries -= 1;
        if (retries === 0) {
          throw lastError;
        }
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
    }
  };

  const uploadChunkedFiles = async (
    file: Blob,
    onProgress: ((pr: number) => void) | undefined
  ) => {
    const totalChunks = Math.ceil(file.size / fileChunkSize);
    const importId = v4();

    for (let chunkNumber = 0; chunkNumber < totalChunks; chunkNumber++) {
      const start = chunkNumber * fileChunkSize;
      const end = Math.min(start + fileChunkSize, file.size);
      const chunk = file.slice(start, end);

      await retryUpload(chunkNumber, chunk, importId, totalChunks, onProgress);
    }

    return { totalChunks, importId };
  };

  const resetProgress = () => setProgress(0);

  return { retryUpload, uploadChunkedFiles, progress, resetProgress };
};
